import { ErrorBoundary } from "@/components/ErrorBoundary/ErrorBoundary";
import { Card, CardContent } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cells, renderers } from "@/jsonforms";
import { jsonFormsEntityToStringMiddleware } from "@/utils/jsonforms";
import { Entity } from "@cohesive/api-entities";

import { JsonForms } from "@jsonforms/react";
import type { ErrorObject } from "ajv";
import { useMemo, useState } from "react";

// Initial schema example
const initialSchema = {
  type: "object",
  required: ["basic_text", "required_number"],
  properties: {
    // Custom Controls
    hubspot_contact: {
      type: "string",
      title: "HubSpot Contact",
    },

    hubspot_company: {
      type: "string",
      title: "HubSpot Company",
    },

    hubspot_deal: {
      type: "string",
      title: "HubSpot Deal",
    },

    hubspot_owner: {
      type: "string",
      title: "HubSpot Owner",
    },

    hubspot_deals_pipeline: {
      type: "string",
      title: "HubSpot Deals Pipeline",
    },

    hubspot_tickets_pipeline: {
      type: "string",
      title: "HubSpot Tickets Pipeline",
    },

    hubspot_deals_pipeline_stage: {
      type: "string",
      title: "HubSpot Deals Pipeline Stage",
    },

    hubspot_tickets_pipeline_stage: {
      type: "string",
      title: "HubSpot Tickets Pipeline Stage",
    },

    slack_user: {
      type: "string",
      title: "Slack User",
      description: "The id of the selected Slack user.",
    },
    slack_users_multi: {
      type: "array",
      title: "Slack Users (Multi)",
      description: "Multiple Slack user IDs.",
      items: {
        type: "string",
      },
    },
    slack_channel: {
      type: "string",
      title: "Slack Channel",
      description: "The id of the selected Slack channel.",
    },

    slack_message: {
      type: "string",
      title: "Slack Message",
      description: "A Slack message in markdown format.",
    },

    slack_files: {
      type: "array",
      title: "Slack Files",
      description: "Files to attach to the Slack message.",
      items: {
        type: "object",
        properties: {
          url: { type: "string" },
          title: { type: "string" },
        },
      },
    },

    google_calendar: {
      type: "string",
      title: "Google Calendar",
      description: "The id of the selected Google Calendar.",
    },

    google_calendar_event: {
      type: "string",
      title: "Google Calendar Event",
      description: "The id of the selected Google Calendar event.",
    },

    github_repository: {
      type: "string",
      title: "GitHub Repository",
      description: "The full name of the selected GitHub repository.",
    },

    zoom_recording: {
      type: "string",
      title: "Zoom Recording",
      description: "The UUID of the selected Zoom recording.",
    },

    asana_workspace: {
      type: "string",
      title: "Asana Workspace",
      description: "The GID of the selected Asana workspace.",
    },

    asana_team: {
      type: "string",
      title: "Asana Team",
      description: "The GID of the selected Asana team.",
    },

    asana_user: {
      type: "string",
      title: "Asana User",
      description: "The GID of the selected Asana user.",
    },

    asana_project: {
      type: "string",
      title: "Asana Project",
      description: "The GID of the selected Asana project.",
    },

    // Basic Controls
    basic_text: {
      type: "string",
      description: "A basic text input",
    },
    required_number: {
      type: "number",
      description: "A required number input",
    },
    disabled_field: {
      type: "string",
      description: "A disabled text input",
    },

    // Number Formats
    integer_field: {
      type: "integer",
      minimum: 0,
      maximum: 100,
    },
    number_slider: {
      type: "number",
      minimum: 0,
      maximum: 100,
    },

    // String Formats
    email_field: {
      type: "string",
      format: "email",
    },
    password_field: {
      type: "string",
      format: "password",
    },
    url_field: {
      type: "string",
      format: "uri",
    },

    // Date/Time
    date_field: {
      type: "string",
      format: "date",
    },
    time_field: {
      type: "string",
      format: "time",
    },
    datetime_field: {
      type: "string",
      format: "date-time",
    },

    // Options
    enum_dropdown: {
      type: "string",
      enum: ["Option 1", "Option 2", "Option 3"],
    },
    enum_radio: {
      type: "string",
      enum: ["Radio 1", "Radio 2", "Radio 3"],
    },
    boolean_toggle: {
      type: "boolean",
      description: "A boolean toggle",
    },

    // Arrays
    simple_array: {
      type: "array",
      items: {
        type: "string",
      },
    },
    complex_array: {
      type: "array",
      items: {
        type: "object",
        properties: {
          title: { type: "string" },
          description: { type: "string" },
          priority: {
            type: "string",
            enum: ["Low", "Medium", "High"],
          },
        },
      },
    },

    // Nested Objects
    address: {
      type: "object",
      properties: {
        street: { type: "string" },
        city: { type: "string" },
        state: { type: "string" },
        zip: { type: "string" },
      },
    },

    // Rich Text
    markdown_field: {
      type: "string",
      description: "A markdown editor",
    },

    // File Upload
    file_upload: {
      type: "string",
      format: "data-url",
    },
  },
};

const initialUiSchema = {
  type: "VerticalLayout",
  elements: [
    {
      type: "Group",
      label: "HubSpot",
      options: { collapsible: true },
      elements: [
        {
          type: "Control",
          label: "HubSpot Contact",
          scope: "#/properties/hubspot_contact",
          options: {
            control: "hubspot_contact",
          },
        },
        {
          type: "Control",
          label: "HubSpot Company",
          scope: "#/properties/hubspot_company",
          options: {
            control: "hubspot_company",
          },
        },
        {
          type: "Control",
          label: "HubSpot Deal",
          scope: "#/properties/hubspot_deal",
          options: {
            control: "hubspot_deal",
          },
        },
        {
          type: "Control",
          label: "HubSpot Owner",
          scope: "#/properties/hubspot_owner",
          options: {
            control: "hubspot_owner",
          },
        },
        {
          type: "Control",
          label: "HubSpot Deals Pipeline",
          scope: "#/properties/hubspot_deals_pipeline",
          options: {
            control: "hubspot_pipeline",
            pipeline_type: "deals",
          },
        },
        {
          type: "Control",
          label: "HubSpot Deals Pipeline Stage",
          scope: "#/properties/hubspot_deals_pipeline_stage",
          options: {
            control: "hubspot_pipeline_stage",
            pipeline_field: "hubspot_deals_pipeline",
            pipeline_type: "deals",
          },
        },
        {
          type: "Control",
          label: "HubSpot Tickets Pipeline",
          scope: "#/properties/hubspot_tickets_pipeline",
          options: {
            control: "hubspot_pipeline",
            pipeline_type: "tickets",
          },
        },
        {
          type: "Control",
          label: "HubSpot Tickets Pipeline Stage",
          scope: "#/properties/hubspot_tickets_pipeline_stage",
          options: {
            control: "hubspot_pipeline_stage",
            pipeline_field: "hubspot_tickets_pipeline",
            pipeline_type: "tickets",
          },
        },
      ],
    },
    {
      type: "Group",
      label: "Slack",
      options: { collapsible: true },
      elements: [
        {
          type: "Control",
          label: "Slack User",
          scope: "#/properties/slack_user",
          options: {
            control: "slack_user",
          },
        },
        {
          type: "Control",
          label: "Slack Users (Multi-Select)",
          scope: "#/properties/slack_users_multi",
          options: {
            control: "slack_user",
          },
        },
        {
          type: "Control",
          label: "Slack Channel",
          scope: "#/properties/slack_channel",
          options: {
            control: "slack_channel",
          },
        },
        {
          type: "Control",
          label: "Slack Message",
          scope: "#/properties/slack_message",
          options: {
            control: "slack_markdown_editor",
          },
        },
        {
          type: "Control",
          label: "Slack Files",
          scope: "#/properties/slack_files",
          options: {
            control: "slack_files",
          },
        },
      ],
    },
    {
      type: "Group",
      label: "Google Calendar",
      options: { collapsible: true },
      elements: [
        {
          type: "Control",
          label: "Google Calendar",
          scope: "#/properties/google_calendar",
          options: {
            control: "google_calendar_calendar",
          },
        },
        {
          type: "Control",
          label: "Google Calendar Event",
          scope: "#/properties/google_calendar_event",
          options: {
            control: "google_calendar_event",
          },
        },
      ],
    },
    {
      type: "Group",
      label: "GitHub",
      options: { collapsible: true },
      elements: [
        {
          type: "Control",
          label: "GitHub Repository",
          scope: "#/properties/github_repository",
          options: {
            control: "github_repository",
          },
        },
      ],
    },
    {
      type: "Group",
      label: "Zoom",
      options: { collapsible: true },
      elements: [
        {
          type: "Control",
          label: "Zoom Recording",
          scope: "#/properties/zoom_recording",
          options: {
            control: "zoom_recording",
          },
        },
      ],
    },
    {
      type: "Group",
      label: "Asana",
      options: { collapsible: true },
      elements: [
        {
          type: "Control",
          label: "Asana Workspace",
          scope: "#/properties/asana_workspace",
          options: {
            control: "asana_workspace",
          },
        },
        {
          type: "Control",
          label: "Asana Team",
          scope: "#/properties/asana_team",
          options: {
            control: "asana_team",
            watchField: "asana_workspace",
          },
        },
        {
          type: "Control",
          label: "Asana User",
          scope: "#/properties/asana_user",
          options: {
            control: "asana_user",
            watchField: "asana_workspace",
          },
        },
        {
          type: "Control",
          label: "Asana Project",
          scope: "#/properties/asana_project",
          options: {
            control: "asana_project",
            watchField: "asana_workspace",
          },
        },
      ],
    },
    {
      type: "Group",
      label: "Basic Inputs",
      options: { collapsible: true },
      elements: [
        {
          type: "Control",
          scope: "#/properties/basic_text",
        },
        {
          type: "Control",
          scope: "#/properties/required_number",
        },
        {
          type: "Control",
          scope: "#/properties/disabled_field",
          options: {
            disabled: true,
          },
        },
      ],
    },
    {
      type: "Group",
      label: "Number Formats",
      options: { collapsible: true },
      elements: [
        {
          type: "Control",
          scope: "#/properties/integer_field",
        },
        {
          type: "Control",
          scope: "#/properties/number_slider",
          options: {
            slider: true,
          },
        },
      ],
    },
    {
      type: "Group",
      label: "String Formats",
      options: { collapsible: true },
      elements: [
        {
          type: "Control",
          scope: "#/properties/email_field",
        },
        {
          type: "Control",
          scope: "#/properties/password_field",
        },
        {
          type: "Control",
          scope: "#/properties/url_field",
        },
      ],
    },
    {
      type: "HorizontalLayout",
      elements: [
        {
          type: "Group",
          label: "Date & Time",
          options: { collapsible: true },
          elements: [
            {
              type: "Control",
              scope: "#/properties/date_field",
            },
            {
              type: "Control",
              scope: "#/properties/time_field",
            },
            {
              type: "Control",
              scope: "#/properties/datetime_field",
            },
          ],
        },
      ],
    },
    {
      type: "Group",
      label: "Options & Toggles",
      options: { collapsible: true },
      elements: [
        {
          type: "Control",
          scope: "#/properties/enum_dropdown",
        },
        {
          type: "Control",
          scope: "#/properties/enum_radio",
          options: {
            format: "radio",
          },
        },
        {
          type: "Control",
          scope: "#/properties/boolean_toggle",
        },
      ],
    },
    {
      type: "Group",
      label: "Arrays",
      options: { collapsible: true },
      elements: [
        {
          type: "Control",
          scope: "#/properties/simple_array",
        },
        {
          type: "Control",
          scope: "#/properties/complex_array",
          options: {
            detail: {
              type: "VerticalLayout",
              elements: [
                {
                  type: "Control",
                  scope: "#/properties/title",
                },
                {
                  type: "Control",
                  scope: "#/properties/description",
                },
                {
                  type: "Control",
                  scope: "#/properties/priority",
                },
              ],
            },
          },
        },
      ],
    },
    {
      type: "Group",
      label: "Nested Objects",
      options: { collapsible: true },
      elements: [
        {
          type: "Control",
          scope: "#/properties/address",
        },
      ],
    },
    {
      type: "Group",
      label: "Advanced Inputs",
      options: { collapsible: true },
      elements: [
        {
          type: "Control",
          scope: "#/properties/markdown_field",
          options: {
            multi: true,
            markdown: true,
          },
        },
        {
          type: "Control",
          scope: "#/properties/file_upload",
        },
      ],
    },
  ],
};

export function isEntity(e: unknown): e is Entity {
  return (
    typeof e === "object" &&
    e !== null &&
    "type" in e &&
    "platform" in e &&
    "data" in e
  );
}

export function JsonFormsTestPage() {
  const [schema, setSchema] = useState(JSON.stringify(initialSchema, null, 2));
  const [uiSchema, setUiSchema] = useState(
    JSON.stringify(initialUiSchema, null, 2),
  );

  const [errors, setErrors] = useState<
    ErrorObject<string, Record<string, any>, unknown>[]
  >([]);

  const [data, setData] = useState({
    basic_text: "Sample text",
    required_number: 42,
    disabled_field: "This field is disabled",
    integer_field: 50,
    number_slider: 75,
    email_field: "example@email.com",
    boolean_toggle: true,
    // simple_array: ["Item 1", "Item 2"],
    // complex_array: [
    //   {
    //     title: "Item 1",
    //     description: "Description of item 1",
    //     priority: "Low",
    //   },
    //   {
    //     title: "Item 2",
    //     description: "Description of item 1",
    //     priority: "High",
    //   },
    // ],
    address: {
      street: "123 Main St",
      city: "Anytown",
      state: "ST",
      zip: "12345",
    },
    slack_message:
      "*Bold text*, _italic text_, ~strikethrough~\n\n> This is a quote\n\n```\ncode block\nwith multiple lines\n```\n\n* List item one\n* List item two\n\n1. Ordered item one\n2. Ordered item two",
    slack_files: [
      {
        url: "https://example.com/image.png",
        title: "Example Image",
      },
      {
        url: "https://example.com/document.pdf",
        title: "Example PDF",
      },
    ],
  });

  const handleSchemaChange = (value: string) => {
    setSchema(value);
  };

  const handleUiSchemaChange = (value: string) => {
    setUiSchema(value);
  };

  const _schema = useMemo(() => {
    try {
      return JSON.parse(schema);
    } catch (e) {
      // Ignore invalid JSON
    }
  }, [schema]);

  const _uiSchema = useMemo(() => {
    try {
      return JSON.parse(uiSchema);
    } catch (e) {
      // Ignore invalid JSON
    }
  }, [uiSchema]);

  return (
    <Card className="w-full max-w-4xl mx-auto">
      <CardContent className="p-6">
        <Tabs defaultValue="demo" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="demo">Demo</TabsTrigger>
            <TabsTrigger value="schema">Schema</TabsTrigger>
            <TabsTrigger value="uischema">UI Schema</TabsTrigger>
            <TabsTrigger value="data">Data</TabsTrigger>
          </TabsList>

          {errors.length > 0 && (
            <div className="p-4 border rounded-lg">
              <h3 className="text-sm font-medium mb-2">Errors</h3>
              <pre className="text-sm overflow-scroll">
                {JSON.stringify(errors, null, 2)}
              </pre>
            </div>
          )}

          <TabsContent value="demo" className="mt-4">
            <div className="p-4 border rounded-lg">
              <ErrorBoundary>
                <JsonForms
                  schema={_schema}
                  uischema={_uiSchema}
                  data={data}
                  renderers={renderers}
                  cells={cells}
                  middleware={jsonFormsEntityToStringMiddleware}
                  onChange={({ data, errors = [] }) => {
                    console.log("data", data);
                    console.log("errors", errors);
                    setErrors(errors || []);
                    setData(data);
                  }}
                />
              </ErrorBoundary>
            </div>
          </TabsContent>

          <TabsContent value="schema" className="mt-4">
            <textarea
              className="w-full h-[400px] font-mono text-sm bg-transparent resize-none outline-hidden"
              value={schema}
              onChange={(e) => handleSchemaChange(e.target.value)}
            />
          </TabsContent>

          <TabsContent value="uischema" className="mt-4">
            <textarea
              className="w-full h-[400px] font-mono text-sm bg-transparent resize-none outline-hidden"
              value={uiSchema}
              onChange={(e) => handleUiSchemaChange(e.target.value)}
            />
          </TabsContent>

          <TabsContent value="data" className="mt-4">
            <ScrollArea className="h-[400px] w-full rounded-md border p-4">
              <pre className="text-sm overflow-scroll">
                {JSON.stringify(data, null, 2)}
              </pre>
            </ScrollArea>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
